import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, Input, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatClntDeliveries, useGetMatClntWarehouses } from '../../../hooks/genericHooks';
import type { MatClntDelivery, MatWarehouses } from '../../../types';
import { insertData } from '../../../services/api';
import { useAuth } from '../../../components/common/security/AuthContext';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

export default function MatClntTxnIncoming() {
  const navigate = useNavigate();
  const { data: delData = [], isLoading } = useGetMatClntDeliveries() as any;
  const { data: whData = [] } = useGetMatClntWarehouses() as any;

  const deliveries: MatClntDelivery[] = Array.isArray(delData) ? delData : (delData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const { user: currentUser } = useAuth();

  // Owned warehouses set for current user
  const ownedWhSet = useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);

  const whOptions = useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) })), [warehouses, ownedWhSet]);
  const hasAnyOwned = useMemo(() => ownedWhSet.size > 0, [ownedWhSet]);

  // If currently selected warehouse is not owned, clear it
  useEffect(() => {
    if (warehouseId != null && !ownedWhSet.has(Number(warehouseId))) {
      setWarehouseId(undefined);
    }
  }, [ownedWhSet]);

  // Controls
  const [warehouseId, setWarehouseId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState<string>('');
  const [shipmentFilter, setShipmentFilter] = useState<string>('');
  const [sizeFilter, setSizeFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [mirrFilter, setMirrFilter] = useState<string[]>([]);
  const [date, setDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [useDeliveryDate, setUseDeliveryDate] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);

  // Selection
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<MatClntDelivery[]>([]);

  const filtered = useMemo(() => {
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const ship = shipmentFilter.trim();
    const sizeSel = (sizeFilter || '').trim();
    const groupSel = (groupFilter || '').trim();
    const mirrSel = Array.isArray(mirrFilter) ? mirrFilter.filter(Boolean) : [];
    const [start, end] = dateRange;
    return (deliveries || []).filter(d => {
      if (tokens.length > 0 || phraseMatches.length > 0) {
        const hay = [
          d.ident_code || '',
          d.short_desc || '',
          d.invoice_no || '',
        ].join(' ').toLowerCase();
        if (phraseMatches.length > 0) {
          if (!phraseMatches.every(p => hay.includes(p))) return false;
        } else if (!tokens.every(t => hay.includes(t))) {
          return false;
        }
      }
      if (ship && String(d.shipment_id || '').indexOf(ship) === -1) return false;
      if (sizeSel && String(d.size_text || '').toLowerCase() !== sizeSel.toLowerCase()) return false;
      if (groupSel && String(d.material_group || '').toLowerCase() !== groupSel.toLowerCase()) return false;
      if (mirrSel.length > 0) {
        const val = String(d.mirr_no || '').toLowerCase();
        if (!mirrSel.some(v => String(v).toLowerCase() === val)) return false;
      }
      if (useDeliveryDate && (start || end)) {
        const dv = d.delivery_date ? new Date(d.delivery_date) : null;
        if (!dv) return false;
        if (start) {
          const ds = new Date(start);
          if (dv < new Date(ds.getFullYear(), ds.getMonth(), ds.getDate())) return false;
        }
        if (end) {
          const de = new Date(end);
          if (dv > new Date(de.getFullYear(), de.getMonth(), de.getDate(), 23,59,59,999)) return false;
        }
      } else if (!useDeliveryDate && date) {
        const dv = d.delivery_date ? new Date(d.delivery_date) : null;
        if (!dv) return false;
        const ds = new Date(date);
        const startOfDay = new Date(ds.getFullYear(), ds.getMonth(), ds.getDate());
        const endOfDay = new Date(ds.getFullYear(), ds.getMonth(), ds.getDate(), 23,59,59,999);
        if (dv < startOfDay || dv > endOfDay) return false;
      }
      return true;
    });
  }, [deliveries, search, shipmentFilter, sizeFilter, groupFilter, mirrFilter, dateRange, date, useDeliveryDate]);
  
  const sizeOptions = useMemo(() => {
    const s = new Map<string, string>();
    (deliveries || [])
      .filter(d => !shipmentFilter || String(d.shipment_id || '').toLowerCase().includes(shipmentFilter.toLowerCase()))
      .filter(d => !groupFilter || String(d.material_group || '').toLowerCase() === groupFilter.toLowerCase())
      .filter(d => !(mirrFilter?.length) || mirrFilter.some(v => String(v).toLowerCase() === String(d.mirr_no || '').toLowerCase()))
      .forEach(d => {
        const raw = (d.size_text || '').trim();
        if (!raw) return; const k = raw.toLowerCase(); if (!s.has(k)) s.set(k, raw);
      });
    return Array.from(s.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentFilter, groupFilter, mirrFilter]);

  const groupOptions = useMemo(() => {
    const s = new Map<string, string>();
    (deliveries || [])
      .filter(d => !shipmentFilter || String(d.shipment_id || '').toLowerCase().includes(shipmentFilter.toLowerCase()))
      .filter(d => !sizeFilter || String(d.size_text || '').toLowerCase() === sizeFilter.toLowerCase())
      .filter(d => !(mirrFilter?.length) || mirrFilter.some(v => String(v).toLowerCase() === String(d.mirr_no || '').toLowerCase()))
      .forEach(d => {
        const raw = (d.material_group || '').trim();
        if (!raw) return; const k = raw.toLowerCase(); if (!s.has(k)) s.set(k, raw);
      });
    return Array.from(s.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentFilter, sizeFilter, mirrFilter]);

  const mirrOptions = useMemo(() => {
    const s = new Map<string, string>();
    (deliveries || [])
      .filter(d => !shipmentFilter || String(d.shipment_id || '').toLowerCase().includes(shipmentFilter.toLowerCase()))
      .filter(d => !sizeFilter || String(d.size_text || '').toLowerCase() === sizeFilter.toLowerCase())
      .filter(d => !groupFilter || String(d.material_group || '').toLowerCase() === groupFilter.toLowerCase())
      .forEach(d => {
        const raw = (d.mirr_no || '').trim();
        if (!raw) return; const k = raw.toLowerCase(); if (!s.has(k)) s.set(k, raw);
      });
    return Array.from(s.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentFilter, sizeFilter, groupFilter]);

  // Auto-clear invalid selections when options narrow due to cascading
  useEffect(() => {
    if (sizeFilter && !sizeOptions.some(o => (o.value as string) === sizeFilter)) setSizeFilter('');
  }, [sizeOptions, sizeFilter]);
  useEffect(() => {
    if (groupFilter && !groupOptions.some(o => (o.value as string) === groupFilter)) setGroupFilter('');
  }, [groupOptions, groupFilter]);
  useEffect(() => {
    if (mirrFilter && mirrFilter.length > 0) {
      const allowed = new Set(mirrOptions.map(o => String(o.value)));
      const next = mirrFilter.filter(v => allowed.has(String(v)));
      if (next.length !== mirrFilter.length) setMirrFilter(next);
    }
  }, [mirrOptions, mirrFilter]);

  const columns: any[] = useMemo(() => [
    { title: 'ItemCode', dataIndex: 'ident_code', key: 'ident_code', width: 140, sorter: (a: MatClntDelivery, b: MatClntDelivery) => (a.ident_code || '').localeCompare(b.ident_code || '') },
    { title: 'Short Desc', dataIndex: 'short_desc', key: 'short_desc', ellipsis: true },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 100 },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Group', dataIndex: 'material_group', key: 'material_group', width: 120 },
    { title: 'Qty', dataIndex: 'delivery_qty', key: 'delivery_qty', width: 100, align: 'right', sorter: (a: MatClntDelivery, b: MatClntDelivery) => (a.delivery_qty ?? 0) - (b.delivery_qty ?? 0) },
    { title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', width: 140,
      render: (v: string) => v ? new Date(v).toLocaleDateString() : '-',
      exportValue: (r: MatClntDelivery) => r.delivery_date ? dayjs(r.delivery_date).format('YYYY-MM-DD') : ''
    },
    { title: 'MIRR No', dataIndex: 'mirr_no', key: 'mirr_no', width: 120 },
    { title: 'Invoice', dataIndex: 'invoice_no', key: 'invoice_no', width: 120 },
    { title: 'Shipment ID', dataIndex: 'shipment_id', key: 'shipment_id', width: 130, sorter: (a: MatClntDelivery, b: MatClntDelivery) => String(a.shipment_id || '').localeCompare(String(b.shipment_id || '')) },
  ], []);

  const selectedCount = useMemo(() => selectedRows.length, [selectedRows]);

  const handleSelectFiltered = () => {
    const keys = (filtered || []).map(r => String((r as any).id));
    setSelectedKeys(keys);
    setSelectedRows(filtered);
  };

  const handleUnselectAll = () => {
    setSelectedKeys([]);
    setSelectedRows([]);
  };

  const handleSubmit = async () => {
    try {
      if (!warehouseId) { message.warning('Please select a destination warehouse'); return; }
      if (!ownedWhSet.has(Number(warehouseId))) { message.error('You are not an owner of the selected warehouse. Incoming transactions are restricted.'); return; }
      if (selectedRows.length === 0) { message.warning('Please select at least one delivery'); return; }
      setSubmitting(true);

      const payloads = selectedRows.map(r => {
        const pickedDate = date || undefined; // Always use transaction date selected by user

        const base: any = {
          op_type: 'incoming',
          itemcode: r.ident_code,
          qty: r.delivery_qty,
          unit: r.unit,
          to_warehouse_id: warehouseId,
          reference_no: `MIRR#${r.mirr_no}`,
        };
        if (pickedDate) base.txn_date = pickedDate; // let DB default apply if not provided
        if (r.long_desc || r.short_desc) base.notes = r.long_desc || r.short_desc || undefined;
        return base;
      });

      await insertData('mat_clnt_txns', payloads);
      message.success(`Created ${payloads.length} incoming transaction(s)`);
      setSelectedKeys([]);
      setSelectedRows([]);
    } catch (err) {
      console.error(err);
      message.error('Failed to create transactions');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Incoming from Client Deliveries</Title>
              <Text type="secondary">Create mat_clnt_txns(op_type='incoming') from selected client deliveries</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <DatePicker.RangePicker
              onChange={(vals) => {
                if (!vals || vals.length !== 2) { setDateRange([null, null]); return; }
                setDateRange([
                  vals[0] ? vals[0].format('YYYY-MM-DD') : null,
                  vals[1] ? vals[1].format('YYYY-MM-DD') : null,
                ]);
              }}
              disabled={!useDeliveryDate}
              disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Destination Warehouse"
              allowClear
              value={warehouseId}
              onChange={(v) => setWarehouseId(v)}
              options={whOptions}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Size"
              allowClear
              showSearch
              value={sizeFilter || undefined}
              onChange={(v) => setSizeFilter(v || '')}
              options={sizeOptions}
              style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Group"
              allowClear
              showSearch
              value={groupFilter || undefined}
              onChange={(v) => setGroupFilter(v || '')}
              options={groupOptions}
              style={{ width: 100 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Input
              placeholder="Search ident/desc/invoice..."
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 200 }}
            />
            <Input
              placeholder="Shipment ID"
              allowClear
              value={shipmentFilter}
              onChange={(e) => setShipmentFilter(e.target.value)}
              style={{ width: 120 }}
            />
            <Select
              placeholder="MIRR No"
              mode="multiple"
              allowClear
              showSearch
              value={mirrFilter}
              onChange={(vals) => setMirrFilter(vals || [])}
              options={mirrOptions}
              style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <DatePicker
              allowClear
              value={date ? dayjs(date) : null}
              onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : null)}
              disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
              style={{ width: 120 }}
            />
            <Button
              onClick={handleSelectFiltered}
              disabled={(filtered?.length || 0) === 0}
            >
              Select filtered{filtered?.length ? ` (${filtered.length})` : ''}
            </Button>
            <Button
              onClick={handleUnselectAll}
              disabled={selectedCount === 0}
            >
              Unselect all{selectedCount ? ` (${selectedCount})` : ''}
            </Button>
            <Button
              action="add"
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!hasAnyOwned}
            >
              Submit Incoming{selectedCount ? ` (${selectedCount})` : ''}
            </Button>
          </Space>
        </Col>
      </Row>
      <StandardTable<MatClntDelivery>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys, rows) => { setSelectedKeys(keys); setSelectedRows(rows as MatClntDelivery[]); },
          preserveSelectedRowKeys: true,
        }}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
